<!--
Hand-rolled week calendar: 7 day columns x one row per meal slot. Entries with no
slot land in the last row. Dates are YYYY-MM-DD strings throughout (they compare
and group lexically, so no date library is needed).

<MealPlanWeekGrid
    :entries="plan.entries"
    v-on:add-entry="({date, slot}) => ..."
    v-on:remove-entry="entry_id => ..."
/>
-->
<template>
    <div class="week_grid_wrap">
        <div class="week_nav">
            <button type="button" class="week_arrow" @click="shiftWeek(-7)">&#8592; {{ $t('meal_plans.week_prev') }}</button>
            <span class="week_label">{{ formatDay(week_dates[0]) }} — {{ formatDay(week_dates[6]) }}</span>
            <button type="button" class="week_arrow" @click="shiftWeek(7)">{{ $t('meal_plans.week_next') }} &#8594;</button>
        </div>

        <div class="week_grid">
            <div class="grid_row header_row">
                <div class="slot_label"></div>
                <div class="day_cell header_cell" v-for="date in week_dates" :key="date" :class="{ today: date === today }">
                    {{ formatDay(date) }}
                </div>
            </div>
            <div class="grid_row" v-for="slot in slots" :key="slot">
                <div class="slot_label">{{ $t('meal_plans.slots.' + slot) }}</div>
                <div class="day_cell" v-for="date in week_dates" :key="date">
                    <div class="entry" v-for="entry in entriesAt(date, slot)" :key="entry.id">
                        <router-link :to="'/recipes/' + entry.recipe_id" class="no_extra_styling entry_name">{{ entry.recipe_id }}</router-link>
                        <span class="entry_servings" v-if="entry.servings">×{{ entry.servings }}</span>
                        <button type="button" class="remove_btn" @click="$emit('remove-entry', entry.id)">✕</button>
                    </div>
                    <button type="button" class="add_btn" @click="$emit('add-entry', { date: date, slot: slot === 'none' ? null : slot })">+</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { parseLocalDate } from '@/utils/date.js';

// Local-timezone YYYY-MM-DD (toISOString would shift the day around midnight).
function toDateString(date) {
    let month = String(date.getMonth() + 1).padStart(2, '0');
    let day = String(date.getDate()).padStart(2, '0');
    return date.getFullYear() + '-' + month + '-' + day;
}

function startOfWeek(date) {
    let copy = new Date(date);
    // Monday-start week: getDay() is 0 for Sunday.
    let day = (copy.getDay() + 6) % 7;
    copy.setDate(copy.getDate() - day);
    return copy;
}

export default {
    name: "MealPlanWeekGrid",
    props: [
        'entries'
    ],
    data() {
        return {
            slots: ['breakfast', 'lunch', 'dinner', 'snack', 'none'],
            week_start: startOfWeek(new Date()),
            today: toDateString(new Date())
        }
    },
    computed: {
        week_dates() {
            let dates = [];
            for (let i = 0; i < 7; i++) {
                let date = new Date(this.week_start);
                date.setDate(date.getDate() + i);
                dates.push(toDateString(date));
            }
            return dates;
        }
    },
    methods: {
        entriesAt(date, slot) {
            return (this.entries || []).filter((entry) => {
                let entry_slot = entry.slot || 'none';
                return entry.date === date && entry_slot === slot;
            });
        },
        shiftWeek(days) {
            let next = new Date(this.week_start);
            next.setDate(next.getDate() + days);
            this.week_start = next;
        },
        formatDay(date_string) {
            return parseLocalDate(date_string).toLocaleDateString('el-GR', { weekday: 'short', day: 'numeric', month: 'numeric' });
        }
    }
}
</script>

<style scoped>
.week_nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}
.week_arrow {
    background: none;
    border: none;
    color: var(--color-primary);
    cursor: pointer;
}
.week_arrow:hover {
    color: var(--color-secondary);
}
.week_label {
    color: var(--color-grey-2);
    font-size: 0.9rem;
}
.week_grid_wrap {
    overflow-x: auto;
}
.week_grid {
    min-width: 760px;
    border: 1px solid var(--color-grey-1);
    border-radius: 6px;
}
.grid_row {
    display: flex;
}
.grid_row + .grid_row {
    border-top: 1px solid var(--color-grey-1);
}
.slot_label {
    flex: 0 0 110px;
    padding: 0.4rem;
    font-size: 0.85rem;
    color: var(--color-grey-2);
    display: flex;
    align-items: center;
}
.day_cell {
    flex: 1;
    min-width: 0;
    padding: 0.3rem;
    border-left: 1px solid var(--color-grey-1);
    text-align: center;
}
.header_cell {
    font-size: 0.85rem;
    font-weight: bold;
    padding: 0.5rem 0.3rem;
}
.header_cell.today {
    color: var(--color-secondary);
}
.entry {
    font-size: 0.8rem;
    border: 1px solid var(--color-grey-1);
    border-radius: 4px;
    padding: 0.2rem;
    margin-bottom: 0.2rem;
    display: flex;
    align-items: center;
    gap: 0.2rem;
}
.entry_name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
}
.entry_name:hover {
    color: var(--color-secondary);
}
.entry_servings {
    color: var(--color-grey-2);
}
.remove_btn {
    background: none;
    border: none;
    color: var(--color-grey-2);
    cursor: pointer;
    padding: 0;
    font-size: 0.7rem;
}
.remove_btn:hover {
    color: var(--color-secondary);
}
.add_btn {
    background: none;
    border: none;
    color: var(--color-grey-1);
    cursor: pointer;
    font-size: 0.9rem;
    padding: 0;
}
.day_cell:hover .add_btn {
    color: var(--color-primary);
}
</style>
